import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { CASE_STUDIES, CONTACT } from '../data/mock';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const CaseStudiesPage = () => {
  const [expandedStudy, setExpandedStudy] = useState(null);

  const toggleStudy = (studyId) => {
    setExpandedStudy(expandedStudy === studyId ? null : studyId);
  };
  return (
    <div className="min-h-screen pt-20 relative overflow-hidden">
      <SEO 
        title="Case Studies | Enterprise Engineering Success Stories"
        description="Explore elite engineering case studies: AI chatbots, Kubernetes infrastructure, desktop applications, and cloud-native solutions. 150+ enterprise projects delivered."
        keywords="software development case studies, full-stack projects, ai solutions, kubernetes deployment, cloud applications"
        url="/case-studies"
      />

      {/* Geometric Backgrounds */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="geometric-pattern" style={{ top: '10%', left: '5%' }} />
        <div className="line-structure" style={{ bottom: '15%', right: '10%', transform: 'rotate(45deg)' }} />
        
        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration large floating" style={{ top: '12%', right: '6%', animationDelay: '0.8s' }} />
        <div className="bento-box-decoration small floating" style={{ bottom: '18%', left: '8%', animationDelay: '2.8s' }} />
        <div className="luxury-gradient-overlay" style={{ top: '25%', right: '12%', width: '340px', height: '340px' }} />
        <div className="corner-bracket top-left" style={{ top: '15%', left: '7%' }} />
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-50/30 via-transparent to-purple-50/30 pointer-events-none" />
        <div className="container max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block mb-6">
              <span className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-purple-200 text-[#4C3362] text-sm font-bold border-2 border-[#4C3362]/30">
                Real-World Engineering Excellence
              </span>
            </div>
            <h1 className="display-lg mb-8">
              Engineering <span className="gradient-text">Success Portfolio</span>
            </h1>
            <p className="text-xl text-slate-700 font-medium leading-relaxed max-w-4xl mx-auto">
              Comprehensive case studies showcasing enterprise-grade solutions across AI integration, 
              cloud infrastructure, and full-stack application development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid - Mosaic Layout */}
      <section className="py-12 bg-white relative">
        <div className="container max-w-7xl">
          <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
            {CASE_STUDIES.map((study, idx) => {
              const isExpanded = expandedStudy === study.id;
              return (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bento-card group"
                >
                  {/* Header - Always Visible */}
                  <div className="mb-4">
                    <div 
                      className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white mb-3"
                      style={{ background: `linear-gradient(135deg, ${study.color}, ${study.color}dd)` }}
                    >
                      {study.subtitle}
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: study.color }}>
                      {study.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{study.description}</p>
                  </div>

                  {/* Expandable Content */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                      {/* Challenge & Solution */}
                      <div className="space-y-4 mb-6 pt-4 border-t border-slate-200">
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2 text-sm">Challenge</h4>
                          <p className="text-slate-600 text-sm leading-relaxed">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2 text-sm">Solution</h4>
                          <p className="text-slate-600 text-sm leading-relaxed">{study.solution}</p>
                        </div>
                      </div>

                      {/* Results */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-900 mb-3 text-sm">Results Achieved</h4>
                        <ul className="space-y-2">
                          {study.results.map((result, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: study.color }} />
                              {result}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 text-sm">Technologies</h4>
                        <div className="flex flex-wrap gap-2">
                          {study.technologies.map((tech, i) => (
                            <div 
                              key={i}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                              style={{ 
                                background: `${study.color}10`,
                                border: `1px solid ${study.color}30`,
                                color: study.color
                              }}
                            >
                              {tech.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                  </AnimatePresence>

                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => toggleStudy(study.id)}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-all hover:bg-slate-50"
                    style={{ color: study.color, border: `2px solid ${study.color}30` }}
                  >
                    {isExpanded ? (
                      <>
                        Show Less <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        View Full Details <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-br from-[#4C3362] via-[#3C284D] to-[#2E2040] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Engineer Your Success</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Transform your vision into a success story. Strategic consultation with 
              elite engineering specialists.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#4C3362] font-bold px-10 py-5 rounded-xl hover:shadow-2xl hover:-translate-y-2 transition-all text-lg"
            >
              Get Started
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container max-w-5xl">
          <ContactForm ctaText="Build Your Success Story With Us" />
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
